/**
 * Moves a returning visitor's stored plans from the old events.json ids to the
 * backend's.
 *
 * Until the switch, every event was keyed by a seven-character id the
 * enrichment script minted ("0menhdc"). The backend keys the same events by
 * UUID. Everything a visitor built in this browser — My Schedule, Save for
 * Later, Not Interested, trims, travel choices, their own edits — is stored
 * against the old ids, and every schedule would empty on the first visit
 * without this.
 *
 * `public/data/legacy-opportunity-ids.json` maps old to new
 * (`{ "<old id>": "<uuid>" }`), built by `scripts/build-legacy-ids.mjs`. Only a
 * browser that holds an old id downloads it. Runs once, before anything reads
 * storage, and is marked done in `mcw.idsMigrated`.
 *
 * An old id with no counterpart is not thrown away. It waits, with its trim,
 * travel choices and edits, in `mcw.legacyUnmapped`, and is tried again
 * whenever the map changes — so an event the backend adds later comes back to
 * whoever saved it.
 */

const KEYS = {
  schedule: 'mcw.schedule',
  saved: 'mcw.saved',
  hidden: 'mcw.hidden',
  attendance: 'mcw.attendance',
  travelLegs: 'mcw.travelLegs',
  eventEdits: 'mcw.eventEdits',
  editOutbox: 'mcw.editOutbox',
} as const;

const MIGRATED_KEY = 'mcw.idsMigrated';
const UNMAPPED_KEY = 'mcw.legacyUnmapped';
/** Which map the unmapped ids were last tried against. */
const MAP_VERSION_KEY = 'mcw.legacyMapVersion';

const MAP_URL = 'data/legacy-opportunity-ids.json';
/** The map is small; a stalled request must not hold the page blank. */
const MAP_TIMEOUT_MS = 4000;

/** Every id the enrichment script minted: seven base-36 characters. */
const LEGACY_ID = /^[0-9a-z]{7}$/;

export const isLegacyId = (id: string): boolean => LEGACY_ID.test(id);

type Entries = Record<string, unknown>;

interface Unmapped {
  schedule: string[];
  saved: string[];
  hidden: string[];
  attendance: Entries;
  travelLegs: Entries;
  /** Already under backend field names. */
  eventEdits: Entries;
}

function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readJson(key: string): unknown {
  const raw = readRaw(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Site data blocked: there is nothing stored to move either.
  }
}

const writeJson = (key: string, value: unknown) => writeRaw(key, JSON.stringify(value));

const isEntries = (value: unknown): value is Entries =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

const entries = (value: unknown): Entries => (isEntries(value) ? value : {});

/** Travel choices are keyed `${dayKey}:${fromId}>${toId}` (travelLegs.legId). */
function parseLegKey(key: string): { day: string; from: string; to: string } | null {
  const match = /^(\d{4}-\d{2}-\d{2}):(.+)>(.+)$/.exec(key);
  return match ? { day: match[1], from: match[2], to: match[3] } : null;
}

function readUnmapped(): Unmapped {
  const stored = entries(readJson(UNMAPPED_KEY));
  return {
    schedule: stringList(stored.schedule),
    saved: stringList(stored.saved),
    hidden: stringList(stored.hidden),
    attendance: entries(stored.attendance),
    travelLegs: entries(stored.travelLegs),
    eventEdits: entries(stored.eventEdits),
  };
}

const isEmpty = (u: Unmapped) =>
  !u.schedule.length &&
  !u.saved.length &&
  !u.hidden.length &&
  !Object.keys(u.attendance).length &&
  !Object.keys(u.travelLegs).length &&
  !Object.keys(u.eventEdits).length;

function legHasLegacyId(key: string): boolean {
  const parsed = parseLegKey(key);
  return Boolean(parsed && (isLegacyId(parsed.from) || isLegacyId(parsed.to)));
}

function hasLegacyIds(): boolean {
  if ([KEYS.schedule, KEYS.saved, KEYS.hidden].some((key) => stringList(readJson(key)).some(isLegacyId))) {
    return true;
  }
  if (Object.keys(entries(readJson(KEYS.attendance))).some(isLegacyId)) return true;
  if (Object.keys(entries(readJson(KEYS.eventEdits))).some(isLegacyId)) return true;
  if (Object.keys(entries(readJson(KEYS.travelLegs))).some(legHasLegacyId)) return true;
  const outbox = Array.isArray(readJson(KEYS.editOutbox)) ? (readJson(KEYS.editOutbox) as unknown[]) : [];
  return outbox.some((n) => isEntries(n) && typeof n.event_id === 'string' && isLegacyId(n.event_id));
}

// --- edits under the backend's field names -------------------------------------------

const RSVP_VALUES: Record<string, string> = {
  'Open to Public': 'open_to_public',
  "Public (RSVP Req'd)": 'registration_required',
  'Apply to Attend': 'apply_to_attend',
  'Invite Only': 'invite_only',
  Paid: 'paid',
  'See Listing': 'see_listing',
};

function formatOf(inPerson: boolean, online: boolean): string {
  if (inPerson && online) return 'hybrid';
  if (inPerson) return 'in_person';
  if (online) return 'virtual';
  return 'see_listing';
}

/**
 * One stored edit from before the move, under the backend's names.
 *
 * Only ever given an edit keyed by an old id: those were all written by the old
 * code, whose `format` was the event-type word rather than the backend's format.
 */
function renameEdit(edit: unknown): unknown {
  if (!isEntries(edit) || !isEntries(edit.fields)) return edit;
  const fields: Entries = {};
  let inPerson: unknown;
  let online: unknown;

  for (const [key, value] of Object.entries(edit.fields)) {
    switch (key) {
      case 'title':
        fields.name = value;
        break;
      case 'venue':
        fields.address = value;
        break;
      case 'rsvp':
        fields.rsvpType = typeof value === 'string' ? (RSVP_VALUES[value] ?? 'see_listing') : value;
        break;
      case 'format':
        fields.eventType = value;
        break;
      case 'coords':
        fields.latitude = Array.isArray(value) ? (value[0] ?? null) : null;
        fields.longitude = Array.isArray(value) ? (value[1] ?? null) : null;
        break;
      case 'url':
        fields.registrationUrl = value;
        break;
      case 'allDay':
        fields.isAllDay = value;
        break;
      case 'inPerson':
        inPerson = value;
        break;
      case 'online':
        online = value;
        break;
      default:
        fields[key] = value;
    }
  }

  if (typeof inPerson === 'boolean' && typeof online === 'boolean') {
    fields.format = formatOf(inPerson, online);
  } else {
    // Half a checkbox pair needs the listing to resolve; eventEdits.ts does that.
    if (typeof inPerson === 'boolean') fields.inPerson = inPerson;
    if (typeof online === 'boolean') fields.online = online;
  }

  return { ...edit, fields };
}

// --- the move ------------------------------------------------------------------------

/** A short, stable fingerprint of the map, so a changed map is noticed. */
function fingerprint(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

async function fetchMap(): Promise<{ ids: Record<string, string>; version: string } | null> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), MAP_TIMEOUT_MS);
  try {
    const res = await fetch(new URL(MAP_URL, document.baseURI).toString(), {
      signal: controller.signal,
      cache: 'no-cache',
    });
    if (!res.ok) return null;
    const text = await res.text();
    const parsed: unknown = JSON.parse(text);
    if (!isEntries(parsed)) return null;
    const ids: Record<string, string> = {};
    for (const [from, to] of Object.entries(parsed)) {
      if (typeof to === 'string') ids[from] = to;
    }
    return { ids, version: fingerprint(text) };
  } catch {
    // Offline, blocked, or not published yet (an SPA fallback serves HTML).
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

function rewrite(ids: Record<string, string>, waiting: Unmapped): Unmapped {
  const unmapped: Unmapped = { schedule: [], saved: [], hidden: [], attendance: {}, travelLegs: {}, eventEdits: {} };

  /** Old ids become new ones; anything else (a UUID, a personal event) passes through. */
  const translate = (id: string): string | null => (isLegacyId(id) ? (ids[id] ?? null) : id);

  const moveList = (key: string, bucket: 'schedule' | 'saved' | 'hidden'): string[] => {
    const out: string[] = [];
    for (const id of [...stringList(readJson(key)), ...waiting[bucket]]) {
      const next = translate(id);
      if (!next) {
        if (!unmapped[bucket].includes(id)) unmapped[bucket].push(id);
      } else if (!out.includes(next)) {
        // Two old ids can name one event: the site listed a few twice.
        out.push(next);
      }
    }
    return out;
  };

  const scheduled = moveList(KEYS.schedule, 'schedule');
  // The two lists are mutually exclusive.
  const onSchedule = new Set(scheduled);
  const saved = moveList(KEYS.saved, 'saved').filter((id) => !onSchedule.has(id));
  const hidden = moveList(KEYS.hidden, 'hidden');
  writeJson(KEYS.schedule, scheduled);
  writeJson(KEYS.saved, saved);
  writeJson(KEYS.hidden, hidden);

  /** Entries already under a new id win over anything translated onto it. */
  const moveEntries = (current: Entries, queued: Entries, bucket: 'attendance' | 'eventEdits', convert = (v: unknown) => v) => {
    const out: Entries = {};
    for (const [id, value] of Object.entries(current)) if (!isLegacyId(id)) out[id] = value;
    const candidates: [string, unknown][] = [
      ...Object.entries(current)
        .filter(([id]) => isLegacyId(id))
        .map(([id, v]): [string, unknown] => [id, convert(v)]),
      ...Object.entries(queued),
    ];
    for (const [id, value] of candidates) {
      const next = translate(id);
      if (!next) unmapped[bucket][id] = value;
      else if (!(next in out)) out[next] = value;
    }
    return out;
  };

  writeJson(KEYS.attendance, moveEntries(entries(readJson(KEYS.attendance)), waiting.attendance, 'attendance'));
  writeJson(KEYS.eventEdits, moveEntries(entries(readJson(KEYS.eventEdits)), waiting.eventEdits, 'eventEdits', renameEdit));

  const legs: Entries = {};
  const currentLegs = entries(readJson(KEYS.travelLegs));
  for (const [key, choice] of Object.entries(currentLegs)) if (!legHasLegacyId(key)) legs[key] = choice;
  for (const [key, choice] of [
    ...Object.entries(currentLegs).filter(([key]) => legHasLegacyId(key)),
    ...Object.entries(waiting.travelLegs),
  ]) {
    const parsed = parseLegKey(key);
    const from = parsed ? translate(parsed.from) : null;
    const to = parsed ? translate(parsed.to) : null;
    if (parsed && from && to) {
      const next = `${parsed.day}:${from}>${to}`;
      if (!(next in legs)) legs[next] = choice;
    } else {
      unmapped.travelLegs[key] = choice;
    }
  }
  writeJson(KEYS.travelLegs, legs);

  // A queued notice about an event with no new id still means something to the
  // team, so it is sent under the old one.
  const outbox = readJson(KEYS.editOutbox);
  if (Array.isArray(outbox)) {
    writeJson(
      KEYS.editOutbox,
      outbox.map((n) =>
        isEntries(n) && typeof n.event_id === 'string' && ids[n.event_id]
          ? { ...n, event_id: ids[n.event_id] }
          : n,
      ),
    );
  }

  return unmapped;
}

export async function migrateLegacyIds(): Promise<void> {
  const migrated = readRaw(MIGRATED_KEY) === '1';
  const waiting = readUnmapped();

  if (migrated ? isEmpty(waiting) : !hasLegacyIds()) {
    if (!migrated) writeRaw(MIGRATED_KEY, '1');
    return;
  }

  const map = await fetchMap();
  // No map yet: everything stays exactly as it is, and the next load tries again.
  if (!map) return;
  if (migrated && readRaw(MAP_VERSION_KEY) === map.version) return;

  const unmapped = rewrite(map.ids, waiting);
  writeRaw(UNMAPPED_KEY, isEmpty(unmapped) ? null : JSON.stringify(unmapped));
  writeRaw(MAP_VERSION_KEY, map.version);
  writeRaw(MIGRATED_KEY, '1');
}
